import {
  ICombo,
  IComboSlot,
  IComboSlotOption,
  ILegacyCartEntry,
  ISanityVendorConfigs,
} from '@rbi-ctg/menu';
import {
  Brand,
  ICartEntryInput,
  IPriceOrderInput,
  PosVendor,
  ServiceMode,
} from 'generated/graphql-gateway';
import { IOffersState } from 'state/global-state/models/loyalty/offers/offers.types';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { buildStoreAddress, frontendToBackendCartEntryMap } from 'utils/cart';
import { CartEntryType } from 'utils/cart/types';
import { getDefaultComboSlotOption } from 'utils/menu/get-default-combo-slot-option';

import { FetchComboFn, IRehHydrateEntriesParams } from './types';

export const isOfferCombo = (cartEntry: ILegacyCartEntry) =>
  cartEntry.type === CartEntryType.offerCombo;

/**
 * Generates a menu URL path for a given cart entry.
 *
 * The URL has the format:
 *   `/menu/{entryType}-{identifier}`
 *
 * - If the cart entry is an offer combo, the `entryType` is set to `"combo"` ( we must do this mapping for cart hydration ).
 *   Otherwise, it uses the cart entry's own type.
 * - The identifier part of the URL is determined by:
 *   - `offerBenefitId`, if provided.
 *   - Otherwise, the cart entry's `_id`.
 *
 * @param {ILegacyCartEntry} cartEntry - The cart entry for which to generate the URL.
 * @param {string} [offerBenefitId] - Optional identifier for offers.
 *   If present, this takes precedence over the cart entry's `_id`.
 *
 * @returns {string} The generated URL.
 */
export const generateCartEntryUrl = (
  cartEntry: ILegacyCartEntry,
  offerBenefitId?: string
): string => {
  const isOffer = isOfferCombo(cartEntry);
  const typeForUrl = isOffer ? CartEntryType.combo : cartEntry.type;
  return `/menu/${typeForUrl.toLowerCase()}-${offerBenefitId ? offerBenefitId : cartEntry._id}`;
};

/**
 * Merges new cart entries returned from a rehydration process with the original cart entries from the current store.
 *
 * Processing steps:
 * 1. Normalizes `sanityId` values by stripping `$` suffixes to match One Menu and Legacy IDs.
 * 2. Searchs for the corresponding original cart entry for each new entry.
 * 3. Preserves selected fields from the original entry:
 *    - `image`, `url`, `enableUpsellPricing`, and `cartId`.
 *    - Excludes properties like `vendorConfigs` and `redeemable` which are not needed post-merge.
 * 4. If the entry is an offer:
 *    - Resolves the associated CMS offer from `cmsOffers`.
 *    - Applies the `offerBenefit._id` as the new `_id` and `sanityId`.
 *    - Regenerates the `url` with `generateCartEntryUrl`.
 * 5. Recursively merges child entries using the same logic.
 *
 * @param {ILegacyCartEntry[]} newCartEntries - Cart entries returned from the backend after rehydration.
 * @param {ILegacyCartEntry[]} originalEntries - The original cart entries before rehydration.
 * @param {(LoyaltyOffer | LoyaltySystemwideTierGroup)[]} cmsOffers - CMS offers used to resolve combo/offer entries.
 *
 * @returns {ILegacyCartEntry[]} A new array of merged cart entries.
 */
export const mergeCartEntries = (
  newCartEntries: ILegacyCartEntry[],
  originalEntries: ILegacyCartEntry[],
  cmsOffers: IOffersState['cmsOffers']
): ILegacyCartEntry[] => {
  const normalizeSanityId = (id: string) => (id?.includes('$') ? id.split('$')[1] : id);

  const tempOriginalCartEntries = [...originalEntries];

  const getOriginalCartEntry = (sanityId: string): ILegacyCartEntry | undefined => {
    const key = normalizeSanityId(sanityId);
    const idx = tempOriginalCartEntries.findIndex(o => normalizeSanityId(o._id) === key);
    if (idx === -1) {
      return undefined;
    }
    return tempOriginalCartEntries.splice(idx, 1)[0];
  };

  const mergedCartEntries: ILegacyCartEntry[] = [];

  for (const entry of newCartEntries) {
    const original = getOriginalCartEntry(entry.sanityId);

    const {
      // Excluding vendorConfigs and redeemable props from the original entry
      vendorConfigs: _ignoredVendorConfigsProp,
      // @ts-ignore
      redeemable: _ignoredRedeemableProp,
      // Pulling original children to be used next to merge the cart entry
      children: originalChildren,
      // Setting originalSafe as the rest of the entry data
      ...originalSafe
    } = (original ?? {}) as ILegacyCartEntry;

    const isOffer = isOfferCombo(entry);
    const offer = isOffer ? cmsOffers.find(offer => offer._id === entry.sanityId) ?? null : null;
    const offerBenefit = offer?.incentives?.[0];
    const offerBenefitId =
      offerBenefit && '_id' in offerBenefit ? (offerBenefit as { _id: string })._id : undefined;

    const mergedEntry: ILegacyCartEntry = {
      ...entry,
      ...originalSafe,

      _id: isOffer && offerBenefitId ? offerBenefitId : original?._id,
      sanityId: isOffer && offerBenefitId ? offerBenefitId : original?._id,
      cartId: original?.cartId!,
      // After rehydration, the lineId is updated by the mutation itself.
      // We should not allow the lineId to change to a different value.
      // Both cartId and lineId props should have the same value to avoid having pricing errors.
      lineId: original?.cartId!,
      type: entry.type,

      ...(original && {
        image: original.image,
        url: generateCartEntryUrl(original, offerBenefitId ?? ''),
        ...(original?.enableUpsellPricing !== undefined && {
          enableUpsellPricing: original.enableUpsellPricing,
        }),
      }),
      children: mergeCartEntries(entry.children ?? [], originalChildren ?? [], cmsOffers),
      // Ensure offer Vendor configs are properly set to reprice carrols offers.
      ...(isOffer
        ? { offerVendorConfigs: (offer.vendorConfigs as ISanityVendorConfigs) || {} }
        : undefined),
    };

    // @ts-ignore
    const { redeemable, ...rest } = mergedEntry;
    mergedCartEntries.push(rest);
  }

  return mergedCartEntries;
};

/**
 * Ensures that the cart entry payload is properly sanitized before running reHydration.
 *
 * This function recursively processes each cart entry and its children to:
 * 1. Remove the `posIdsConfiguration` property, which is not required for hydration.
 * 2. Normalize `_id` and `sanityId` values by stripping suffixes (e.g., `$` variants)
 *    to ensure consistency between One Menu and Legacy identifiers.
 * 3. Guarantee a valid quantity:
 *    - For `item` type entries, if quantity is missing or set to `0`, it defaults to `1`.
 *    - Otherwise, the original quantity is preserved.
 * 4. Recursively sanitize children cart entries using the same logic.
 *
 * @param {ILegacyCartEntry[]} entries - The list of cart entries to sanitize.
 * @returns {ILegacyCartEntry[]} A new array of sanitized cart entries, ready for hydration/reHydration.
 */
export function sanitizeCartEntries(entries: ILegacyCartEntry[]): ILegacyCartEntry[] {
  return entries.map(entry => {
    // 1. Exclude posIdsConfiguration prop and get the children for recursive call.
    const { posIdsConfiguration, children, ...rest } = entry;

    // 2. Normalize ids ( we need to do this since one menu has different ids )
    const _id = rest._id.includes('$') ? rest._id.split('$')[1] : rest._id;
    const newSanityId = rest.sanityId.includes('$') ? rest.sanityId.split('$')[1] : rest.sanityId;

    // 3. Prepare quantity ( this prevents hydration and reHydration mutations to fail )
    const fixedQuantity =
      rest.type === CartEntryType.item && (!rest.quantity || rest.quantity === 0)
        ? 1
        : rest.quantity;

    return {
      ...rest,
      _id,
      sanityId: newSanityId,
      quantity: fixedQuantity,
      children: sanitizeCartEntries(children ?? []),
    };
  });
}

interface IFillMissingSanityDataParams {
  cartEntries: ILegacyCartEntry[];
  fetchCombo: FetchComboFn;
}

const updateChildrenForMissingSanityData = (
  children: ILegacyCartEntry[] | undefined,
  combo: ICombo
): ILegacyCartEntry[] | undefined => {
  if (!children) {
    return children;
  }

  return children.map(child => {
    if (child.type === CartEntryType.comboSlot) {
      const matchingSanityComboSlot = combo.options?.find(
        comboSlot => comboSlot?._id === child.sanityId
      );

      const cartEntryComboSlotItem = child.children[0];

      if (matchingSanityComboSlot && cartEntryComboSlotItem?.sanityId) {
        const matchingComboSlotOption = matchingSanityComboSlot.options?.find(
          opt =>
            ((opt as unknown) as IComboSlotOption)?.option?._id === cartEntryComboSlotItem.sanityId
        );

        if (matchingComboSlotOption) {
          return {
            ...child,
            defaultComboSlotOption: getDefaultComboSlotOption(
              matchingSanityComboSlot as IComboSlot
            ),
            children: child.children.map(item =>
              item.sanityId === cartEntryComboSlotItem.sanityId
                ? {
                    ...item,
                    enableUpsellPricing: matchingComboSlotOption.enableUpsellPricing,
                  }
                : item
            ),
          };
        }
      }
    }
    return child;
  });
};

/**
 * Ensures that all missing cart entry information is populated before hydration/rehydration.
 *
 * This function iterates through the provided cart entries and:
 * 1. Identifies `combo` type entries that have children (`comboSlot`s) missing required Sanity data.
 *    - Specifically, it checks for missing or undefined `defaultComboSlotOption`.
 * 2. Updates the affected combo entry by:
 *    - Populating its `mainItem` field.
 *    - Regenerating its `url` with `generateCartEntryUrl`.
 *    - Updating its children with the newly fetched Sanity data via `updateChildrenForMissingSanityData`.
 *
 * This ensures that cart entries contain all necessary information for hydration
 * and rehydration processes, especially for repricing flows.
 *
 * @param {IFillMissingSanityDataParams} params - Parameters for the operation.
 * @param {ILegacyCartEntry[]} params.cartEntries - The list of cart entries to process.
 * @param {FetchComboFn} params.fetchCombo - Function used to fetch missing combo data from Sanity.
 *
 * @returns {Promise<ILegacyCartEntry[]>} A Promise resolving to the updated list of cart entries,
 * with all missing Sanity data filled in where necessary.
 */
export const fillMissingSanityData = async ({
  cartEntries,
  fetchCombo,
}: IFillMissingSanityDataParams): Promise<ILegacyCartEntry[]> => {
  const updatedEntries = [...cartEntries];

  for (const entry of updatedEntries) {
    if (entry.type === CartEntryType.combo) {
      const hasMissingSanityData = entry.children?.some(
        child =>
          child.type === CartEntryType.comboSlot &&
          (!('defaultComboSlotOption' in child) || !child.defaultComboSlotOption)
      );

      if (hasMissingSanityData) {
        const comboData = await fetchCombo({ variables: { id: entry.sanityId } });
        const combo = comboData.data?.Combo as ICombo;

        if (!combo) {
          continue;
        }

        entry.mainItem = combo.mainItem;
        entry.url = generateCartEntryUrl(entry);
        entry.children = updateChildrenForMissingSanityData(entry.children, combo) ?? [];
      }
    }
  }

  return updatedEntries;
};

/**
 * Rehydrates cart entries when switching stores, ensuring they are compatible with the new selected store.
 *
 * This function performs the following steps:
 * 1. **Sanitize entries**: Cleans the provided cart entries to normalize ids and quantities.
 * 2. **Build rehydration input**: Constructs an `IPriceOrderInput` including store/vendor info,
 *    service mode, and cart entries.
 * 3. **Run rehydration mutation**: Calls the `rehydrateCart` GraphQL mutation with the new store’s ID
 *    and prepared input.
 * 4. **Merge results**: Combines the rehydrated cart entries from the backend with the original cart entries.
 * 5. **Fill missing data**: Uses `fillMissingSanityData` to retrieve any missing Sanity information
 *    (e.g., combo slots) required for pricing.
 *
 * @param {IRehHydrateEntriesParams} params - Parameters for the rehydration process.
 * @param {ILegacyCartEntry[]} params.cartEntries - Current cart entries to be rehydrated.
 * @param {IStore} params.store - The current store context.
 * @param {IStore} params.newStore - The new store to which the cart is being rehydrated.
 * @param {ServiceMode} params.serviceMode - The current service mode (e.g., Pickup, Delivery).
 * @param {Function} params.rehydrateCart - GraphQL mutation function to call the backend rehydrateCart.
 * @param {FetchComboFn} params.fetchCombo - Function to fetch combo details from Sanity when needed.
 * @param {(LoyaltyOffer | ISystemwideTierGroupFragment)[]} params.cmsOffers - CMS offers used to resolve loyalty combo offers.
 *
 * @returns {Promise<ILegacyCartEntry[]>} A Promise resolving to the fully rehydrated cart entries,
 * enriched with missing Sanity data and ready for repricing.
 */
export const rehydrateEntries = async ({
  cartEntries,
  store,
  newStore,
  serviceMode,
  rehydrateCart,
  fetchCombo,
  cmsOffers,
}: IRehHydrateEntriesParams): Promise<ILegacyCartEntry[]> => {
  const sanitizedCartEntries = sanitizeCartEntries(cartEntries);

  const entriesInput = (sanitizedCartEntries.map(cartEntry => {
    return frontendToBackendCartEntryMap(cartEntry);
  }) as unknown) as ICartEntryInput[];

  const input: IPriceOrderInput = {
    requestedAmountCents: 0,
    posVendor: store.pos?.vendor as PosVendor,
    serviceMode: serviceMode! as ServiceMode,
    brand: Brand.BK,
    cartEntries: entriesInput,
    storeAddress: buildStoreAddress(store),
    storeId: store.number ?? '',
  };

  const rehydrateResult = await rehydrateCart({
    variables: { storeId: newStore.number ?? '', input },
  });

  const mergedCartEntries = mergeCartEntries(
    (rehydrateResult.data?.rehydrateCart.cartEntries as unknown) as ILegacyCartEntry[],
    sanitizedCartEntries,
    cmsOffers
  );

  const entries = await fillMissingSanityData({
    cartEntries: mergedCartEntries,
    fetchCombo,
  });

  return entries;
};

/**
 * Maps legacy cart entries for Full Cart hydration flow.
 *
 * This function aligns full cart entries with their corresponding applied loyalty offers.
 * It updates the cart entry ids and URLs to be compatible between legacy and one menu stores.
 *
 * @param {ILegacyCartEntry[]} cartEntries - The legacy cart entries to map.
 * @param {LoyaltyAppliedOffer[]} appliedOffers - The applied loyalty offers.
 *
 * @returns {ILegacyCartEntry[]} A new array of cart entries with updated identifiers and URLs, ready for hydration.
 */
export const mapLegacyEntriesForHydration = (
  cartEntries: ILegacyCartEntry[],
  appliedOffers: LoyaltyAppliedOffer[]
) => {
  const appliedOffersById = new Map(appliedOffers.map(offer => [offer.cartId, offer]));

  return cartEntries.map(cartEntry => {
    const appliedOffer = appliedOffersById.get(cartEntry.cartId);
    const isOffer = isOfferCombo(cartEntry);

    const _id = isOffer && appliedOffer?.cmsId ? appliedOffer.cmsId : cartEntry._id;

    return {
      ...cartEntry,
      _id,
      sanityId: _id,
      url: generateCartEntryUrl(cartEntry, appliedOffer?.cmsId),
    };
  });
};
